import React, { useEffect, useState } from 'react';
import styles from './SalaEspera.module.css';
import HBox from '../../components/layout/HBox';
import CardCliente from './CardCliente';
import CardEspera from './CardEspera';
import { Droppable } from 'react-beautiful-dnd';
import { Tooltip } from '../../components/layout/Tooltip';
import Icon from '@/components/Icon';
import { mdiInformationSlabBox, mdiClockOutline } from '@mdi/js';
import Descricao from '../../components/tipografia/Descricao';

const getListStyle = (isDraggingOver: boolean, isDisabledGroup: boolean, isForaExpediente: boolean, isOffline: boolean) => ({
  background: isDisabledGroup
    ? 'var(--tc-color-gray-100)'
    : isDraggingOver
      ? 'var(--tc-color-primary-100)'
      : isForaExpediente
        ? 'var(--tc-color-warning-50)'
        : isOffline ? 'var(--tc-color-danger-50)' : 'var(--tc-color-gray-200)',
  padding: '4px',
  display: 'flex',
  overflow: 'auto',
  minHeight: '80px',
  minWidth: '225px',
  width: '100%',
  borderRadius: '16px',
  gap: '4px',
  opacity: isDisabledGroup ? 0.6 : 1,
});

type SalaEsperaProps = {
  nome: string;
  descricao: string;
  clientes?: {
    id: number;
    fila_id?: number;
    nome: string;
    status: 'aguardando_atendente' | 'atendimento' | 'atualizando';
    _type?: 'cliente' | 'espera';
    id_grupo?: number;
    cliente_nome?: string | null;
    ultimo_motivo?: string | null;
    canal_cliente_id?: string | null;
    canal_id?: number | null;
  }[];
  dragItem: any;
  droppableId: string;
  online?: boolean;
  requiresGrupo?: boolean;
  hasOnlineAttendants?: boolean;
  aceitaAtendimento?: boolean;
  dentroExpediente?: boolean;
};

const SalaEspera = ({
  nome,
  descricao,
  clientes,
  dragItem,
  droppableId,
  online,
  requiresGrupo = false,
  hasOnlineAttendants,
  aceitaAtendimento = true,
  dentroExpediente = true,
}: SalaEsperaProps) => {
  // deal with StrictMode...
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  const isWaiting = droppableId === 'aguardando_atendente';
  const isGroup = !isWaiting && !online && online === undefined;
  const isUser = online !== undefined;
  const isDisabledGroup = isGroup && !aceitaAtendimento;
  const isForaExpediente = isGroup && !dentroExpediente;
  const isGrupoSemAtendentes = isGroup && !isDisabledGroup && !isForaExpediente && !hasOnlineAttendants;

  let tooltipText = 'Sala de espera';
  if (isDisabledGroup) {
    tooltipText = 'Departamento não aceita atendimentos';
  } else if (isGroup) {
    tooltipText = hasOnlineAttendants
      ? 'Grupo / Departamento com atendentes online'
      : 'Grupo / Departamento sem atendentes online';
  } else if (isUser) {
    tooltipText = online ? 'Atendente online' : 'Atendente offline';
  }

  const nomeColor = !isUser || online ? undefined : 'var(--tc-color-danger-600)';
  const dropDisabled = isDisabledGroup || !!(requiresGrupo && (!dragItem || (dragItem?.grupo_responsavel_id == null && dragItem?._type !== 'espera')));
  const isOffline = !isWaiting && ((!isGroup && !online) || (isGroup && !hasOnlineAttendants));

  const total = clientes?.length ?? 0;
  const contagemLabel = total === 0
    ? '0 atendimentos'
    : total === 1
      ? '1 atendimento'
      : `${total} atendimentos`;

  const hallClassName = isWaiting
    ? styles.hallEntradaWaiting
    : isDisabledGroup
      ? styles.hallEntradaGroupDisabled
      : isForaExpediente
        ? styles.hallEntradaGroupForaExpediente
        : isGroup
          ? hasOnlineAttendants ? styles.hallEntradaGroup : styles.hallEntradaGroupOffline
          : online
            ? styles.hallEntradaUserOnline
            : styles.hallEntradaUserOffline;

  return (
    <HBox>
      <div className={hallClassName}>
        <Descricao>
          <HBox gap="2px">
            <Tooltip position="right" text={tooltipText}>
              <Icon
                path={mdiInformationSlabBox}
                size={1}
                color="var(--tc-color-gray-500)"
              />
            </Tooltip>
            <span
              style={{
                color: nomeColor,
                fontWeight: 600,
                fontSize: '16px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {nome}
            </span>
            {isForaExpediente && (
              <Tooltip
                position="right"
                text="Fora do horário de expediente — atendimentos aguardarão o próximo horário"
              >
                <Icon
                  path={mdiClockOutline}
                  size={0.8}
                  color="var(--tc-color-warning)"
                />
              </Tooltip>
            )}
          </HBox>
        </Descricao>
        <div style={{ textAlign: 'center', fontSize: '12px' }}>{descricao}</div>
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--tc-color-gray-500)' }}>{contagemLabel}</div>
      </div>
      <HBox gap="12px" style={{ flex: 1 }}>
        <Droppable
          droppableId={droppableId}
          direction="horizontal"
          isDropDisabled={dropDisabled}
        >
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              style={{
                ...getListStyle(
                  snapshot.isDraggingOver,
                  isDisabledGroup,
                  isForaExpediente,
                  isOffline
                ),
              }}
            >
              {clientes?.map((cliente, idx) =>
                cliente._type === 'espera' ? (
                  <CardEspera
                    key={`espera-${cliente.id}`}
                    item={cliente as any}
                    index={idx}
                  />
                ) : (
                  <CardCliente
                    key={`${cliente.id}-${idx}`}
                    item={cliente}
                    index={idx}
                    dragItem={dragItem}
                  />
                )
              )}

              {isDisabledGroup && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '13px',
                    color: 'var(--tc-color-gray-500)',
                  }}
                >
                  Departamento configurado para não aceitar novos atendimentos
                </div>
              )}

              {!isDisabledGroup && isForaExpediente && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '13px',
                    color: 'var(--tc-color-warning-700)',
                  }}
                >
                  Fora do horário do expediente
                </div>
              )}

              {isGrupoSemAtendentes && (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '13px',
                    color: 'var(--tc-color-danger-600)',
                  }}
                >
                  Sem atendentes na tela de chats
                </div>
              )}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </HBox>
    </HBox>
  );
};
export default SalaEspera;
